import React, { ChangeEvent, Fragment, useEffect, useMemo, useState } from "react";

import { Box, Container, Divider, FormLabel, IconButton, Radio, RadioGroup, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import PlacesAutocomplete from "components/PlacesAutocomplete";
import ReadyTimeMessage from "containers/OrderTypeDrawer/ReadyTimeMessage";
import OrderTypePickUp from "containers/OrderTypeDrawer/SelectPickUpDateTimeDesktop";
import { AvailableOrderTypes } from "gql/types/globals";
import useBusiness from "hooks/useBusiness";
import useCart from "hooks/useCart";
import EditCircle from "icons/EditCircle";

const useStyles = makeStyles(theme => {
	return {
		container: {
			display: "flex",
			flexDirection: "column",
			padding: 0,
			paddingTop: theme.spacing(1)
		},
		titleRow: {
			marginBottom: theme.spacing(1),
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between"
		},
		titleBlock: {
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-between"
		},
		iconButton: {
			padding: 0,
			marginLeft: theme.spacing(1),
			"&:hover": {
				backgroundColor: "transparent"
			}
		},
		icon: {
			marginRight: 10,
			fill: "#fff",
			[theme.breakpoints.up(480)]: {
				fill: theme.palette.text.primary
			}
		},
		title: {
			fontSize: 14,
			textTransform: "uppercase",
			fontWeight: 500,
			color: "#fff",
			[theme.breakpoints.up(480)]: {
				color: theme.palette.text.primary
			}
		},
		data: {
			fontSize: 12,
			textTransform: "uppercase",
			paddingBottom: theme.spacing(1)
		},
		form: {
			padding: 0
		},
		textField: {
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(2),
			borderColor: "#fff",
			color: "#000",
			backgroundColor: "#f2f2f2",
			borderRadius: 8
		},
		items: {
			marginBottom: theme.spacing(2)
		},
		itemDivider: {
			marginTop: theme.spacing(2),
			backgroundColor: "#f2f2f2"
		},
		item: {
			marginLeft: theme.spacing(1),
			marginTop: theme.spacing(1),
			marginRight: theme.spacing(1),
			display: "flex",
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "space-between"
		},
		itemTitle: {
			color: "#f2f2f2",
			textTransform: "uppercase",
			fontSize: "12px",
			fontWeight: 500,
			letterSpacing: "0.38px",
			[theme.breakpoints.up(480)]: {
				color: theme.palette.text.secondary
			}
		},
		itemTitleChecked: {
			color: "#fff",
			[theme.breakpoints.up(480)]: {
				color: theme.palette.text.primary
			}
		},
		radio: {
			color: "#f2f2f2",
			"&$checked": {
				color: theme.palette.primary.main
			},
			borderColor: "#fff"
		},
		checked: {},
		radioIcon: {
			borderRadius: "50%",
			width: 13,
			height: 13,
			border: "1px solid #f2f2f2",
			fill: theme.palette.text.primary
		},
		radioIconChecked: {
			borderRadius: "50%",
			width: 13,
			height: 13,
			border: "1px solid #fff",
			color: theme.palette.primary.main,
			backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
			checked: {
				color: theme.palette.primary.main
			},
			"&:before": {
				display: "block",
				width: 11,
				height: 11,
				backgroundImage: "radial-gradient(#BDBDBD,#BDBDBD 56%,transparent 32%)",
				content: '""'
			}
		}
	};
});

interface OrderTypeInfoProps {
	onSubmit: (isValid: boolean) => void;
}

const OrderTypeInfo: React.FC<OrderTypeInfoProps> = ({ onSubmit }) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const {
		orderType,
		phoneNumber,
		email,
		fullName,
		deliveryAddress,
		setOrderType,
		getCustomerInfo,
		setCustomerInfo
	} = useCart();
	const { business } = useBusiness();
	const [editMode, setEditMode] = useState(
		orderType?.orderType !== AvailableOrderTypes.pickup && !getCustomerInfo().deliveryAddress
	);

	useEffect(() => {
		submitCallback();
		onSubmit(true);
	}, [onSubmit]);

	function submitCallback() {}

	const value = orderType?.id;

	const handleChange = useMemo(
		() => (event: ChangeEvent<HTMLInputElement> | null, value: string) => {
			const orderType = business.orderTypes.find(({ id }) => id === value);

			if (orderType) {
				setOrderType(orderType);
			}
		},
		[business.orderTypes, setOrderType]
	);

	return (
		<Container className={classes.container}>
			<Box className={classes.titleRow}>
				<Box className={classes.titleBlock}>
					<Typography className={classes.title}>
						{t("for")} {orderType ? orderType?.orderType : "-"}
					</Typography>
					<IconButton className={classes.iconButton} onClick={() => setEditMode(!editMode)} title={t("edit")}>
						<EditCircle width={16} height={16} className={classes.icon} />
					</IconButton>
				</Box>
			</Box>
			{editMode ? (
				<Box className={classes.form}>
					<RadioGroup className={classes.items} aria-label="option" value={value} onChange={handleChange}>
						{business.orderTypes.map(({ id, orderType, name, deliveryRadiusMeters }) => (
							<Fragment key={id}>
								<Divider className={classes.itemDivider} />

								<FormLabel className={classes.item}>
									<Typography className={clsx(classes.itemTitle, { [classes.itemTitleChecked]: value === id })}>
										{name}
									</Typography>
									<Radio
										inputProps={{ "aria-label": name }}
										icon={<span className={classes.radioIcon} />}
										classes={{ root: classes.radio, checked: classes.checked }}
										color="secondary"
										value={id}
									/>
								</FormLabel>

								{value === id && orderType !== AvailableOrderTypes.pickup ? (
									<PlacesAutocomplete
										className={classes.textField}
										placeholder={t("deliveryAddress")}
										defaultValue={deliveryAddress?.place}
										origin={business?.address?.coordinates}
										radius={deliveryRadiusMeters || undefined}
										onChange={(event, value) => {
											setCustomerInfo({
												phoneNumber,
												email,
												fullName,
												deliveryAddress: value && {
													address1: value.structured_formatting.main_text,
													address2: null,
													address3: null,
													city: value.structured_formatting.secondary_text,
													state: null,
													postalCode: null,
													country: null,
													placeId: value.place_id,
													place: value
												}
											});
										}}
									/>
								) : null}

								<OrderTypePickUp visible={value === id} />
							</Fragment>
						))}
					</RadioGroup>
				</Box>
			) : (
				<Box className={classes.titleRow}>
					<Box className={classes.titleBlock}>
						<Typography className={classes.title}>
							<ReadyTimeMessage />
						</Typography>
					</Box>
				</Box>
			)}
		</Container>
	);
};

export default OrderTypeInfo;
